import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Modal, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faArrowUp, faArrowDown, faSync } from '@fortawesome/free-solid-svg-icons';
// Import from Firebase services
import { getServices, addService, updateService, deleteService, cleanupDuplicateServices, syncAllServicesToTreatments } from '../../services/firebase/serviceService';
import { compressImageFile } from '../../services/firebase/imageUtils';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  const [validated, setValidated] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Load services on component mount
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await getServices();
      setServices(data);
      setError(null);
    } catch (err) {
      setError('Failed to load services. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (service = null) => {
    // Make sure we create a new object to avoid reference issues
    if (service) {
      setCurrentService({
        ...service,
        originalImage: service.image // Store the original image URL
      });
    } else {
      setCurrentService({
        title: '',
        description: '',
        image: '',
        acceptsInsurance: false
      });
    }
    setImagePreview(service ? service.image : null);
    setValidated(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentService(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentService({
      ...currentService,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image file is too large. Please select an image under 10MB.');
      return;
    }

    try {
      setError(null);
      const compressedDataUrl = await compressImageFile(file);
      setImagePreview(compressedDataUrl);
      setCurrentService((prev) => ({
        ...prev,
        image: compressedDataUrl
      }));
    } catch (err) {
      console.error('Error processing image:', err);
      setError(err.message || 'Error processing the image. Please try another image.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    // Additional validation to ensure we have the required fields
    if (!currentService.title || !currentService.description) {
      setError('Please fill in all required fields');
      setValidated(true);
      return;
    }
    
    try {
      // Create a clean copy of the service data to avoid reference issues
      const serviceToSave = { ...currentService };
      
      if (serviceToSave.id) {
        // Update existing service
        await updateService(serviceToSave.id, serviceToSave);
      } else {
        // Add new service
        await addService(serviceToSave);
      }
      
      // Refresh the services list
      fetchServices();
      handleCloseModal();
    } catch (err) {
      setError('Failed to save service. Please try again.');
      console.error('Error saving service:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteService(id);
        // Refresh the services list
        fetchServices();
      } catch (err) {
        setError('Failed to delete service. Please try again.');
        console.error(err);
      }
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm('This will remove duplicate services (services with the same title), keeping only the oldest one of each. Continue?')) {
      return;
    }
    try {
      setLoading(true);
      const result = await cleanupDuplicateServices();
      await fetchServices();
      setError(null);
      window.alert(`Cleanup complete. Removed ${result.removed} duplicate(s). ${result.kept} unique service(s) remain.`);
    } catch (err) {
      setError('Failed to clean up duplicates. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToTreatments = async () => {
    if (!window.confirm('This will mirror every service into the Treatments collection so they appear in the Book Appointment dropdown. Continue?')) {
      return;
    }
    try {
      setLoading(true);
      const result = await syncAllServicesToTreatments();
      setError(null);
      window.alert(`Sync complete. ${result.created} new treatment(s) created and ${result.updated} existing treatment(s) updated. Total services: ${result.total}.`);
    } catch (err) {
      setError('Failed to sync services to treatments. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle moving service up in order
  const handleMoveUp = async (index) => {
    if (index > 0) {
      const newServices = [...services];
      const currentService = newServices[index];
      const previousService = newServices[index - 1];
      
      // Swap the order values
      const tempOrder = currentService.order || index;
      currentService.order = previousService.order || (index - 1);
      previousService.order = tempOrder;
      
      // Swap positions in array
      [newServices[index], newServices[index - 1]] = [newServices[index - 1], newServices[index]];
      setServices(newServices);
      
      // Update in Firebase
      try {
        await updateService(currentService.id, { ...currentService, order: currentService.order });
        await updateService(previousService.id, { ...previousService, order: previousService.order });
      } catch (error) {
        console.error('Error updating order:', error);
        setError('Failed to update order. Please try again.');
      }
    }
  };

  // Handle moving service down in order
  const handleMoveDown = async (index) => {
    if (index < services.length - 1) {
      const newServices = [...services];
      const currentService = newServices[index];
      const nextService = newServices[index + 1];
      
      // Swap the order values
      const tempOrder = currentService.order || index;
      currentService.order = nextService.order || (index + 1);
      nextService.order = tempOrder;
      
      // Swap positions in array
      [newServices[index], newServices[index + 1]] = [newServices[index + 1], newServices[index]];
      setServices(newServices);
      
      // Update in Firebase
      try {
        await updateService(currentService.id, { ...currentService, order: currentService.order });
        await updateService(nextService.id, { ...nextService, order: nextService.order });
      } catch (error) {
        console.error('Error updating order:', error);
        setError('Failed to update order. Please try again.');
      }
    }
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Manage Services</h4>
              <div className="d-flex gap-2">
                <Button variant="outline-success" onClick={handleSyncToTreatments} disabled={loading}>
                  <FontAwesomeIcon icon={faSync} className="me-2" />
                  Sync to Treatments
                </Button>
                <Button variant="outline-danger" onClick={handleCleanupDuplicates} disabled={loading}>
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Remove Duplicates
                </Button>
                <Button variant="primary" onClick={() => handleShowModal()}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add New Service
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              {loading ? (
                <div className="text-center p-4">Loading services...</div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center">No services found. Add your first service!</td>
                      </tr>
                    ) : (
                      services.map((service, index) => (
                        <tr key={service.id}>
                          <td>
                            {service.title}
                            {service.acceptsInsurance && (
                              <Badge bg="info" className="ms-2">Insurance</Badge>
                            )}
                          </td>
                          <td>{service.description.substring(0, 100)}...</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {/* Ordering buttons */}
                              <div className="d-flex flex-column">
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm" 
                                  className="mb-1"
                                  onClick={() => handleMoveUp(index)}
                                  disabled={index === 0}
                                  title="Move up"
                                  style={{ minWidth: '32px', padding: '4px' }}
                                >
                                  <FontAwesomeIcon icon={faArrowUp} />
                                </Button>
                                <Button 
                                  variant="outline-secondary" 
                                  size="sm"
                                  onClick={() => handleMoveDown(index)}
                                  disabled={index === services.length - 1}
                                  title="Move down"
                                  style={{ minWidth: '32px', padding: '4px' }}
                                >
                                  <FontAwesomeIcon icon={faArrowDown} />
                                </Button>
                              </div>
                              
                              {/* Edit and Delete buttons */}
                              <div className="d-flex gap-2">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  onClick={() => handleShowModal(service)}
                                  title="Edit"
                                  style={{ minWidth: '32px' }}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleDelete(service.id)}
                                  title="Delete"
                                  style={{ minWidth: '32px' }}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Service Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentService && currentService.id ? 'Edit Service' : 'Add New Service'}
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="serviceTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={currentService?.title || ''}
                onChange={handleInputChange}
                required
              />
              <Form.Control.Feedback type="invalid">
                Please provide a title.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="serviceDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={currentService?.description || ''}
                onChange={handleInputChange}
                required
              />
              <Form.Control.Feedback type="invalid">
                Please provide a description.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="serviceImage">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                required={!currentService?.image}
              />
              <Form.Control.Feedback type="invalid">
                Please provide an image.
              </Form.Control.Feedback>
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Service preview"
                    style={{ maxWidth: '100%', maxHeight: '200px' }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="serviceAcceptsInsurance">
              <Form.Check
                type="checkbox"
                name="acceptsInsurance"
                label="Accepts Insurance"
                checked={!!currentService?.acceptsInsurance}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                When enabled, an "Accepts Insurance" tag is shown on this service on the homepage.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminServices;